<template>
  <date-picker
    v-bind="$attrs"
    color="brand"
    :model-value="dateValue"
    :first-day-of-week="firstDayOfWeek"
    :locale="userLocale"
    :attributes="dayPickerAttributes"
    :masks="{ weekdays: 'WW', input: 'YYYY-MM-DD' }"
    :min-date="minDate"
    :max-date="maxDate"
    :popover="{ visibility: 'click', modifiers: [{ name: 'preventOverflow', options: { mainAxis: false } }] }"
    :is-required="required"
    @update:model-value="$emit('update:modelValue', $event)"
    @click.stop
  >
    <template #default="{ inputValue, inputEvents: { input, ...inputEvents } }">
      <div class="relative flex">
        <calendar-icon
          class="icon-m absolute left-3 top-3 stroke-gray-50"
          :class="{
            '!top-2': small,
          }"
        />
        <ui-input
          class="pl-9"
          :small="small"
          :model-value="inputValue && formatDateIntl(inputValue.concat(' ', '00:00:00'), dateFormat)"
          :placeholder="placeholder"
          @update:model-value="input"
          v-on="inputEvents"
        />
      </div>
    </template>
  </date-picker>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { DatePicker } from 'v-calendar'
import { CalendarIcon } from '@klausapp/ui-kit/icons'
import { UiInput } from '@klausapp/ui-kit'

import { formatDateIntl, maxFourDigitYearDate } from '@/utils/date'
import { session } from '@/composables/useSession'
import { dayPickerAttributes, firstDayOfWeek } from '@/styles/v-calendar'

type DateType = string | Date

const props = withDefaults(
  defineProps<{
    label?: string
    modelValue?: DateType
    minDate?: DateType
    maxDate?: DateType
    dateFormat?: Intl.DateTimeFormatOptions
    small?: boolean
    placeholder?: string
    /**
     * If "true", then the date value can not be deselected by clicking on the same date,
     * neither it can be deselected by clearing the text input.
     */
    required?: boolean
  }>(),
  {
    dateFormat: () => ({ dateStyle: 'medium' }) as Intl.DateTimeFormatOptions,
    maxDate: () => maxFourDigitYearDate,
  },
)

defineEmits<{
  (e: 'update:modelValue', value: DateType): void
}>()

const dateValue = computed(() => {
  if (props.minDate && new Date(props.minDate as DateType) > new Date(props.modelValue as DateType))
    return props.minDate
  return props.modelValue
})

const userLocale = computed(() => session.user.locale.split('-')[0])
</script>

<style lang="scss" src="@/styles/calendar.scss"></style>
